import { useEffect, useRef } from 'react';
import type { GameServices } from '@/app/services';
import type { Game } from '@/game/Game';
import type { Panel } from '@/ui/components/NavButtons';

const PAN_PX = 80;
const ZOOM_STEP = 1.15;

const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

/**
 * Desktop shortcuts (ux-screens §6.3). Placement keys (R, Delete, arrows on a selected item, Ctrl+Z/Y) live in
 * the PlacementController; Esc is the back stack; Backquote is "Sếp tới!". Never steals browser shortcuts
 * (anything with Ctrl/Cmd/Alt) and stays quiet while typing, in a dialogue, a mini game or photo mode.
 */
export function useShortcuts({
  game,
  services,
  panel,
  setPanel,
  paused,
}: {
  game: Game | null;
  services: GameServices;
  panel: Panel;
  setPanel: (p: Panel) => void;
  paused: boolean;
}): void {
  const latest = useRef({ panel, paused });
  useEffect(() => {
    latest.current = { panel, paused };
  });

  useEffect(() => {
    if (!game) return;
    const toggle = (p: Panel) => {
      game.clearSelection();
      setPanel(latest.current.panel === p ? 'none' : p);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      const { panel: open, paused: blocked } = latest.current;
      if (blocked || open === 'photo' || game.miniGameUi.getState().run) return;
      if (document.querySelector('[data-testid="dialogue"]')) return;
      const s = services.store.getState();
      const room = game.activeRoomId ?? 'home';
      let handled = true;
      switch (e.code) {
        case 'Digit1':
          setPanel('none');
          void game.goToRoom('home');
          break;
        case 'Digit2':
          if (s.scenes.includes('townmap')) toggle('town');
          break;
        case 'Digit3':
          toggle('tasks');
          break;
        case 'Digit4':
          toggle('catalog');
          break;
        case 'Digit5':
          setPanel('none');
          document.querySelector<HTMLElement>('[data-testid="bag-tray"] button')?.focus();
          break;
        case 'KeyE':
          if (s.scenes.includes('minimart')) void game.goToRoom(room === 'home' ? 'minimart' : 'home');
          break;
        case 'KeyN':
          // Studio NANO: walk there, or open the Sổ hẹn when already in it.
          if (!s.scenes.includes('studio')) handled = false;
          else if (room === 'studio') toggle('studio');
          else {
            setPanel('none');
            void game.goToRoom('studio');
          }
          break;
        case 'KeyO':
          if (room === 'minimart' && s.unlockedIds.includes('unl_npc_orders')) toggle('orders');
          break;
        case 'KeyP':
          if (s.features.includes('photoMode')) setPanel('photo');
          break;
        case 'KeyM':
          services.dispatch({ type: 'updateSettings', patch: { muted: !s.settings.muted } });
          break;
        case 'Digit0':
        case 'Numpad0':
          game.recenter();
          break;
        case 'Equal':
        case 'NumpadAdd':
          game.nudgeCamera(0, 0, ZOOM_STEP);
          break;
        case 'Minus':
        case 'NumpadSubtract':
          game.nudgeCamera(0, 0, 1 / ZOOM_STEP);
          break;
        case 'KeyW':
          game.nudgeCamera(0, PAN_PX);
          break;
        case 'KeyS':
          game.nudgeCamera(0, -PAN_PX);
          break;
        case 'KeyA':
          game.nudgeCamera(PAN_PX, 0);
          break;
        case 'KeyD':
          game.nudgeCamera(-PAN_PX, 0);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          // With an item selected the arrows move it (PlacementController); otherwise they pan.
          if (game.hasSelection || open !== 'none') {
            handled = false;
            break;
          }
          const d = {
            ArrowUp: [0, PAN_PX],
            ArrowDown: [0, -PAN_PX],
            ArrowLeft: [PAN_PX, 0],
            ArrowRight: [-PAN_PX, 0],
          }[e.code];
          game.nudgeCamera(d[0] ?? 0, d[1] ?? 0);
          break;
        }
        case 'Slash':
          if (e.shiftKey) toggle('shortcuts');
          else handled = false;
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [game, services, setPanel]);
}
